import { config } from '../config';

class WebSocketClient {
    private socket: WebSocket | null;
    private reconnectAttempts: number;
    private maxReconnectAttempts: number;
    private reconnectDelay: number;
    private maxReconnectDelay: number;
    private listeners: Record<string, Array<(...args: unknown[]) => void>>;
    private isConnected: boolean;
    private connectionPromise: Promise<void> | null;
    private heartbeatInterval: ReturnType<typeof setInterval> | null;
    private heartbeatTimeout: ReturnType<typeof setTimeout> | null;
    private heartbeatIntervalTime: number;
    private heartbeatTimeoutTime: number;
    private reconnectTimer: ReturnType<typeof setTimeout> | null;
    private intentionalClose: boolean;

    constructor() {
        this.socket = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 10;
        this.reconnectDelay = 2000;
        this.maxReconnectDelay = 30000;
        this.listeners = {};
        this.isConnected = false;
        this.connectionPromise = null;
        this.heartbeatInterval = null;
        this.heartbeatTimeout = null;
        this.heartbeatIntervalTime = 30000;
        this.heartbeatTimeoutTime = 45000;
        this.reconnectTimer = null;
        this.intentionalClose = false;
    }

    private emit(event: string, ...args: unknown[]) {
        const handlers = this.listeners[event];
        if (handlers) {
            handlers.forEach(fn => fn(...args));
        }
    }

    connect() {
        if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
            return this.connectionPromise || Promise.resolve();
        }

        this.intentionalClose = false;
        this.connectionPromise = new Promise((resolve, reject) => {
            const wsUrl = config.WS_URL || `ws://${window.location.hostname}:8787/ws`;
            console.log(`[WS] Connecting to ${wsUrl}...`);

            try {
                this.socket = new WebSocket(wsUrl);
            } catch (err) {
                console.error('[WS] Failed to create WebSocket:', err);
                reject(err);
                return;
            }

            this.socket.onopen = () => {
                console.log('[WS] Connected');
                this.isConnected = true;
                this.reconnectAttempts = 0;
                this.reconnectDelay = 2000;
                this.startHeartbeat();
                resolve();
            };

            this.socket.onerror = (error) => {
                console.warn('[WS] Error:', error);
                this.emit('error', error);
            };

            this.socket.onclose = (event) => {
                this.isConnected = false;
                this.stopHeartbeat();
                if (!this.intentionalClose) {
                    console.log(`[WS] Disconnected (code: ${event.code}), reconnecting...`);
                    this.scheduleReconnect();
                }
                this.emit('disconnected', event);
            };

            this.socket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    this.emit('message', data);
                    this.emit(data.type || 'unknown', data.payload || data);
                } catch {
                    console.warn('[WS] Could not parse message:', event.data);
                }
            };
        });

        return this.connectionPromise;
    }

    private scheduleReconnect() {
        if (this.reconnectTimer) {return;}
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.reconnectAttempts++;
            this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, this.maxReconnectDelay);
            if (this.reconnectAttempts <= this.maxReconnectAttempts) {
                console.log(`[WS] Reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} (delay: ${this.reconnectDelay}ms)`);
                this.connect();
            } else {
                console.error('[WS] Max reconnect attempts reached');
                this.emit('maxReconnectsReached');
            }
        }, this.reconnectDelay);
    }

    private startHeartbeat() {
        this.stopHeartbeat();
        this.heartbeatInterval = setInterval(() => {
            if (this.socket && this.socket.readyState === WebSocket.OPEN) {
                this.socket.send(JSON.stringify({ type: 'ping' }));
                this.heartbeatTimeout = setTimeout(() => {
                    console.warn('[WS] Heartbeat timeout — closing socket');
                    this.socket?.close();
                }, this.heartbeatTimeoutTime);
            }
        }, this.heartbeatIntervalTime);
    }

    private stopHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
        if (this.heartbeatTimeout) {
            clearTimeout(this.heartbeatTimeout);
            this.heartbeatTimeout = null;
        }
    }

    disconnect() {
        this.intentionalClose = true;
        this.stopHeartbeat();
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        if (this.socket) {
            this.socket.close();
            this.socket = null;
        }
        this.isConnected = false;
    }

    send(data: Record<string, unknown>) {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify(data));
            return true;
        }
        console.warn('[WS] Cannot send — not connected');
        return false;
    }

    on(event: string, callback: (...args: unknown[]) => void) {
        if (!this.listeners[event]) {
            this.listeners[event] = [];
        }
        this.listeners[event].push(callback);
    }

    off(event: string, callback: (...args: unknown[]) => void) {
        const handlers = this.listeners[event];
        if (handlers) {
            this.listeners[event] = handlers.filter(fn => fn !== callback);
        }
    }
}

export const wsClient = new WebSocketClient();
